import { useEffect, useState } from "react";
import { History, LogIn, FileEdit } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import api from "@/api";
import { BUREAU_ROLES, formatDateTime } from "@/constants";

const ACTION_LABELS = [
  [/^login$/, "Connexion au site"],
  [/POST \/api\/admin\/events$/, "Création d'un événement"],
  [/PATCH \/api\/admin\/events\//, "Modification d'un événement"],
  [/DELETE \/api\/admin\/events\//, "Suppression d'un événement"],
  [/POST \/api\/admin\/news$/, "Publication d'une actualité"],
  [/PATCH \/api\/admin\/news\//, "Modification d'une actualité"],
  [/DELETE \/api\/admin\/news\//, "Suppression d'une actualité"],
  [/POST \/api\/admin\/transactions$/, "Ajout d'une écriture de trésorerie"],
  [/DELETE \/api\/admin\/transactions\//, "Suppression d'une écriture de trésorerie"],
  [/POST \/api\/admin\/members$/, "Création d'un membre"],
  [/PATCH \/api\/admin\/members\//, "Modification d'un membre"],
  [/PATCH \/api\/admin\/users\//, "Modification d'un compte (rôle, fonction, statut)"],
  [/POST \/api\/admin\/volunteers$/, "Ajout d'un bénévole"],
  [/PATCH \/api\/admin\/volunteers\//, "Modification d'un bénévole"],
  [/DELETE \/api\/admin\/volunteers\//, "Suppression d'un bénévole"],
  [/POST \/api\/upload/, "Envoi d'un fichier"],
  [/POST \/api\/admin\/documents/, "Ajout d'un document"],
  [/DELETE \/api\/admin\/documents\//, "Suppression d'un document"],
];

const labelFor = (action) => ACTION_LABELS.find(([re]) => re.test(action))?.[1] || action;

export default function AdminActivity() {
  const [logs, setLogs] = useState(null);

  useEffect(() => {
    api.get("/admin/activity-logs").then((r) => setLogs(r.data)).catch(() => setLogs([]));
  }, []);

  return (
    <div className="space-y-6" data-testid="admin-activity-page">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-slate-800 flex items-center justify-center shrink-0">
          <History className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" data-testid="admin-activity-title">Journal d'activité</h1>
          <p className="text-sm text-slate-500 mt-0.5">Connexions et modifications — trace horodatée de chaque passage, visible par tout le bureau.</p>
        </div>
      </div>

      {logs === null ? (
        <p className="text-sm text-slate-400">Chargement…</p>
      ) : logs.length === 0 ? (
        <Card className="border-slate-200"><CardContent className="p-6 text-sm text-slate-500">Aucune activité enregistrée pour le moment.</CardContent></Card>
      ) : (
        <Card className="border-slate-200 overflow-hidden">
          <div className="divide-y divide-slate-100" data-testid="activity-log-list">
            {logs.map((log) => (
              <div key={log.id} className="px-4 sm:px-6 py-3.5 flex flex-wrap items-center gap-x-4 gap-y-1" data-testid={`activity-log-${log.id}`}>
                <span className="flex items-center gap-2 w-44 shrink-0 text-xs text-slate-500 tabular-nums">
                  {log.action === "login" ? (
                    <LogIn className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                  ) : (
                    <FileEdit className="h-3.5 w-3.5 text-sky-600 shrink-0" />
                  )}
                  {formatDateTime(log.created_at)}
                </span>
                <span className="min-w-0">
                  <span className="text-sm font-semibold text-slate-800">
                    {log.first_name} {log.last_name}
                  </span>
                  {log.bureau_role ? (
                    <span className="ml-2 inline-block text-[11px] font-semibold bg-emerald-100 text-emerald-800 rounded-full px-2 py-0.5 align-middle">
                      {BUREAU_ROLES[log.bureau_role] || log.bureau_role}
                    </span>
                  ) : null}
                  <span className="block text-xs text-slate-400 truncate">{log.email}</span>
                </span>
                <span className="ml-auto text-sm text-slate-600" data-testid={`activity-action-${log.id}`}>{labelFor(log.action)}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
